import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  ImagePlus, RefreshCw, CheckCircle2,
  AlertCircle, X, ImageOff,
} from 'lucide-react';
import { logoAPI } from '../../api/logo';
import PageHeader from '../../components/ui/PageHeader';
import Button     from '../../components/ui/Button';

// ── Constants ─────────────────────────────────────────────────
const MAX_FILE_BYTES = 15 * 1024 * 1024; // 15 MB

// Storage::url() returns a relative path like /storage/logo/logo.png.
// Prepend the backend origin so the browser loads from the correct server.
const BACKEND_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api')
  .replace(/\/api$/, '');

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_ORIGIN}${url}`;
};
const ALLOWED_TYPES  = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
const ALLOWED_LABEL  = 'JPG, PNG or SVG — max 15 MB';

// ── Helpers ──────────────────────────────────────────────────
const formatDateTime = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

// ── Main page ────────────────────────────────────────────────
export default function LogoPage() {
  const fileInputRef = useRef(null);

  // current logo (API #16)
  const [current,      setCurrent]      = useState(null); // { logo_url, updated_at }
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError,   setFetchError]   = useState('');

  // upload form (API #15)
  const [imageFile,  setImageFile]  = useState(null);
  const [preview,    setPreview]    = useState(null);
  const [fileError,  setFileError]  = useState('');
  const [uploading,  setUploading]  = useState(false);
  const [apiError,   setApiError]   = useState('');
  const [toast,      setToast]      = useState('');

  // ── GET /api/admin/logo ───────────────────────────────────
  const fetchLogo = useCallback(async () => {
    setFetchLoading(true);
    setFetchError('');
    try {
      const { data } = await logoAPI.getCurrent();
      if (data.status) setCurrent(data.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setCurrent(null); // no logo yet — show empty state
      } else {
        setFetchError(
          err?.response?.data?.message || 'Failed to load current logo.'
        );
      }
    } finally {
      setFetchLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogo(); }, [fetchLogo]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── File selection ────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError('Only JPG, PNG or SVG files are allowed');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError('File size must not exceed 2 MB');
      return;
    }

    setFileError('');
    setApiError('');
    setImageFile(file);

    // SVG can't use createObjectURL reliably for preview in all browsers — use FileReader
    if (file.type === 'image/svg+xml') {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      if (preview) URL.revokeObjectURL(preview);
      setPreview(URL.createObjectURL(file));
    }
  };

  const clearSelection = () => {
    setImageFile(null);
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    setPreview(null);
    setFileError('');
    setApiError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── POST /api/admin/logo ──────────────────────────────────
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!imageFile) { setFileError('Please select a logo file'); return; }

    const fd = new FormData();
    fd.append('logo', imageFile);

    setUploading(true);
    setApiError('');
    try {
      const { data } = await logoAPI.upload(fd);
      if (data.status) {
        setCurrent(data.data);       // update displayed logo in-place
        clearSelection();
        setToast('Logo updated successfully');
      } else {
        setApiError(data.message || 'Upload failed.');
      }
    } catch (err) {
      setApiError(
        err?.response?.data?.message ||
        err?.response?.data?.errors?.logo?.[0] ||
        'Something went wrong. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-0">
      <PageHeader title="Logo" />

      <div className="flex flex-col gap-4 p-4">

        {/* ── Success toast ── */}
        {toast && (
          <div className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm animate-fade-in"
               style={{
                 background: 'rgba(5,150,105,0.15)',
                 border: '1px solid rgba(5,150,105,0.35)',
                 color: '#6ee7b7',
               }}>
            <CheckCircle2 size={15} className="flex-shrink-0" />
            <span>{toast}</span>
          </div>
        )}

        {/* ── Current logo card (API #16) ── */}
        <div className="card flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-widest"
               style={{ color: 'rgba(212,160,23,0.6)' }}>
              Current Logo
            </p>
            <button
              onClick={fetchLogo}
              disabled={fetchLoading}
              className="flex h-7 w-7 items-center justify-center rounded-xl
                         transition-all active:scale-90 disabled:opacity-50"
              style={{ background: 'rgba(255,255,255,0.07)' }}
              aria-label="Refresh"
            >
              <RefreshCw size={13}
                className={`text-white ${fetchLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {fetchLoading ? (
            <div className="skeleton h-28 w-28 rounded-2xl mx-auto" />
          ) : fetchError ? (
            <p className="text-sm text-center py-4" style={{ color: '#fca5a5' }}>
              {fetchError}
            </p>
          ) : current ? (
            <div className="flex flex-col items-center gap-3">
              {/* Logo preview */}
              <div className="flex h-28 w-28 items-center justify-center rounded-2xl overflow-hidden"
                   style={{
                     background: 'rgba(255,255,255,0.08)',
                     border: '1px solid rgba(212,160,23,0.25)',
                   }}>
                <img
                  src={resolveImageUrl(current.logo_url)}
                  alt="Current logo"
                  className="max-h-full max-w-full object-contain p-2"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden h-full w-full items-center justify-center">
                  <ImageOff size={24} style={{ color: 'rgba(255,255,255,0.2)' }} />
                </div>
              </div>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Last updated {formatDateTime(current.updated_at)}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 gap-2">
              <div className="flex h-14 w-14 items-center justify-center rounded-full"
                   style={{ background: 'rgba(255,255,255,0.06)' }}>
                <ImageOff size={22} style={{ color: 'rgba(255,255,255,0.25)' }} />
              </div>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
                No logo uploaded yet
              </p>
            </div>
          )}
        </div>

        {/* ── Upload form (API #15) ── */}
        <div className="card flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg flex-shrink-0"
                 style={{ background: 'rgba(212,160,23,0.15)' }}>
              <ImagePlus size={14} style={{ color: '#d4a017' }} />
            </div>
            <h3 className="font-serif text-sm font-semibold text-white">
              {current ? 'Replace Logo' : 'Upload Logo'}
            </h3>
          </div>

          {/* API error */}
          {apiError && (
            <div className="flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs"
                 style={{
                   background: 'rgba(239,68,68,0.12)',
                   border: '1px solid rgba(239,68,68,0.3)',
                   color: '#fca5a5',
                 }}>
              <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
              <span>{apiError}</span>
            </div>
          )}

          <form onSubmit={handleUpload} noValidate className="flex flex-col gap-4">

            {/* File picker */}
            <div className="flex flex-col gap-1.5">
              <label className="input-label">{ALLOWED_LABEL}</label>

              {!preview ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-2 rounded-xl
                             border-2 border-dashed py-10 transition-all"
                  style={{
                    borderColor: fileError
                      ? 'rgba(239,68,68,0.5)'
                      : 'rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.03)',
                  }}
                >
                  <ImagePlus size={26} style={{ color: 'rgba(255,255,255,0.3)' }} />
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    Tap to select logo file
                  </span>
                </button>
              ) : (
                <div className="relative flex items-center justify-center rounded-xl overflow-hidden"
                     style={{
                       minHeight: '140px',
                       background: 'rgba(255,255,255,0.06)',
                       border: '1px solid rgba(212,160,23,0.2)',
                     }}>
                  <img
                    src={preview}
                    alt="Logo preview"
                    className="max-h-32 max-w-full object-contain p-4"
                  />
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="absolute top-2 right-2 flex h-7 w-7 items-center
                               justify-center rounded-full transition-opacity hover:opacity-80"
                    style={{ background: 'rgba(0,0,0,0.55)' }}
                  >
                    <X size={13} className="text-white" />
                  </button>
                  {imageFile && (
                    <span className="absolute bottom-2 left-2 rounded-lg px-2 py-0.5
                                     text-[10px] font-medium"
                          style={{ background: 'rgba(0,0,0,0.6)', color: 'rgba(255,255,255,0.7)' }}>
                      {imageFile.name}
                    </span>
                  )}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/svg+xml"
                onChange={handleFileChange}
                className="hidden"
              />

              {fileError && (
                <p className="text-xs" style={{ color: '#fca5a5' }}>{fileError}</p>
              )}
            </div>

            {/* Warning when replacing */}
            {current && imageFile && (
              <p className="text-xs px-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
                The existing logo will be permanently replaced and the old file deleted from storage.
              </p>
            )}

            <Button
              type="submit"
              variant="gold"
              fullWidth
              loading={uploading}
              disabled={!imageFile}
            >
              {current ? 'Replace Logo' : 'Upload Logo'}
            </Button>

          </form>
        </div>

      </div>
    </div>
  );
}
